"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import useGetImportations from "@/hooks/requests/importation/use-get-importations";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IImportation from "@/interfaces/importation.interface";
import { ImportationsMessages } from "@/messages/importations/importations.messages";
import { IFilter } from "@/types/filter.types";

import ImportationFiltersForm from "./components/FiltersForm";
import { ImportationTable } from "./components/Table";
import { initialFilterValues } from "./constants";

type Props = {
  messages: ImportationsMessages;
};

export default function ImportationsPage({ messages }: Props) {
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<IImportation>({ initialValue: initialFilterValues });
  const filters = filter.filters;
  const getImportations = useGetImportations({ filters, pageNumber, pageSize });
  const { data, error, isLoading } = getImportations;

  const shouldLoadImportationTable = !!data && data.items.length > 0 && !isLoading;
  const shouldLoadError = !data && !!error && !isLoading;
  const shouldLoadNoImportationsFound = !!data && data.items.length === 0 && !error && !isLoading;

  const handleChangeFilters = (newFilters: IFilter<IImportation>[]) => {
    filter.changeFilters(newFilters);
  };

  return (
    <PageLayout messages={messages} title={messages.importationsPageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={handleChangeFilters}>
          <ImportationFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              {shouldLoadImportationTable && (
                <ImportationTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.importationErrorToLoad}
                  description={error.message}
                  alt={messages.importationNotFoundAlt}
                />
              )}

              {shouldLoadNoImportationsFound && (
                <AlertBlock
                  title={messages.importationNotFoundError}
                  description={messages.importationNotFoundDescription}
                  alt={messages.importationNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
